import {
  createContext, useCallback, useContext, useEffect, useRef, useState
} from 'react';
import * as PropTypes from 'prop-types';
import jwt_decode from 'jwt-decode';
import { sessionStatuses } from './utils/Consts';

const AuthContext = createContext({
  jwtToken: '',
  email: '',
  authenticate: () => {},
  extendSession: () => {},
  sessionState: sessionStatuses.LOGGED_OUT,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};

const aboutToExpireTime =  5 * 60 * 1000; // 5 minutes
const timeCheckInterval = 10 * 1000; // 10 seconds

export function AuthProvider({ children }) {
  const [jwtToken, setJwtToken] = useState('');
  const [email, setEmail] = useState('');
  const [sessionState, setSessionState] = useState(sessionStatuses.LOGGED_OUT);

  const triedToLoginRef = useRef(false);
  const timeCheckIntervalIdRef = useRef(null);

  const saveAndMonitorAccessToken = useCallback((token) => {
    const { exp } = jwt_decode(token);
    localStorage.setItem('jwtToken', token);
    setJwtToken(token);
    setSessionState(sessionStatuses.LOGGED_IN);

    if (timeCheckIntervalIdRef.current) clearInterval(timeCheckIntervalIdRef.current);
    timeCheckIntervalIdRef.current = setInterval(() => {
      const msToExpire = exp * 1000 - Date.now();
      if (msToExpire <= 0) {
        setSessionState(sessionStatuses.EXPIRED);
        setJwtToken('');
        setEmail('');
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('email');
      } else if (msToExpire <= aboutToExpireTime) {
        setSessionState(sessionStatuses.ALMOST_EXPIRED);
      }
    }, timeCheckInterval);
  }, []);

  const updateEmailFromToken = useCallback(async (token) => {
    const decodedToken = jwt_decode(token);
    try {
      const res = await fetch(`${process.env.REACT_APP_CCVI_API_URL}/auth/get-user-email`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userEmail = res.ok ? await res.text() : (decodedToken.sub || 'Unknown user');
      setEmail(userEmail);
      localStorage.setItem('email', userEmail);
    } catch (err) {
      console.error('Error fetching user email:', err);
    }
  }, [])

  const tryToLogin = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    // first check localStorage for tokens:
    let localToken = localStorage.getItem('jwtToken');
    let isExpired = false;

    if (localToken) {
      try {
        const { exp } = jwt_decode(localToken);
        isExpired = exp * 1000 < Date.now();
      } catch (err) {
        isExpired = true;
      }
    }

    // If localStorage doesn't have a valid token, but there's a code in the URL, try to use it:
    if (code && (!localToken || isExpired)) {
      try {
        const res = await fetch(`${process.env.REACT_APP_CCVI_API_URL}/auth/auth-token-from-code?redirect_uri=${window.location.origin}&code=${code}`);
        if (res.ok) {
          const { accessToken } = await res.json();
          localToken = accessToken;
          isExpired = false;
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error('Token from code error:', err);
      }
    }

    if (localToken && !isExpired) {
      updateEmailFromToken(localToken);
      saveAndMonitorAccessToken(localToken);
    }

  }, [saveAndMonitorAccessToken, updateEmailFromToken]);

  useEffect(() => {
    // Avoid trying to login twice with the same token, due to useEffects re-running in strict mode:
    if (!triedToLoginRef.current) {
      triedToLoginRef.current = true;
      tryToLogin();
    }
  }, [tryToLogin]);

  const authenticate = useCallback(async () => {
    const response = await fetch(`${process.env.REACT_APP_CCVI_API_URL}/auth/auth-url/?redirect_uri=${window.location.origin}&scope=email%20openid&state=`);
    const loginUrl = await response.text();
    window.location.href = loginUrl;
  }, []);

  const value = {
    jwtToken,
    email,
    sessionState,
    authenticate
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.any.isRequired,
};
